/**
 * ElementCounter Hook
 *
 * Counts descendant elements with `data-counter-label` grouped by their value,
 * then updates elements with matching `data-counter-display` with the count.
 *
 * You can optionally specify a `data-counter-event` on the container to push the counts
 * to the server as a LiveView event.
 * The event payload will include the container's ID and the counts object.
 *
 * Recounts on mount and after every LiveView DOM patch. On destroy, pushes
 * one final event with an empty `counts` object so the server can clear
 * any state it was keeping for this element.
 *
 * Usage:
 *
 *   <div phx-hook="ElementCounter" id="my-container" data-counter-event="update_counts">
 *     <!-- sends { id: "my-container", counts: { conflict: 2, add: 1 } } to the server -->
 *
 *     <!-- Elements to count (e.g. rendered dynamically by LiveView) -->
 *     <span data-counter-label="conflict">...</span>
 *     <span data-counter-label="conflict">...</span>
 *     <span data-counter-label="add">...</span>
 *
 *     <!-- Display elements (updated automatically with the count) -->
 *     <span data-counter-display="conflict">0</span>  <!-- becomes "2" -->
 *     <span data-counter-display="add">0</span>        <!-- becomes "1" -->
 *   </div>
 */
const ElementCounter = {
  mounted(this: any) {
    const counts = this.updateCounts();
    const eventName = this.el.dataset.counterEvent;

    if (eventName) this.pushEvent(eventName, { id: this.el.id, counts });
  },
  updated(this: any) {
    this.mounted();
  },
  destroyed(this: any) {
    const eventName = this.el.dataset.counterEvent;

    if (eventName) this.pushEvent(eventName, { id: this.el.id, counts: {} });
  },
  updateCounts(this: any) {
    const counts: Record<string, number> = {};

    this.el
      .querySelectorAll("[data-counter-label]")
      .forEach((el: HTMLElement) => {
        const label = el.dataset.counterLabel!;
        counts[label] = (counts[label] || 0) + 1;
      });

    for (const [label, count] of Object.entries(counts)) {
      this.el
        .querySelectorAll(`[data-counter-display="${label}"]`)
        .forEach((el: HTMLElement) => {
          el.textContent = String(count);
        });
    }

    return counts;
  },
};

export default ElementCounter;
